import {
    BaseSdkClient,
} from "@mrflark/core/api";
import type {
    BaseSdkClientOptions,
} from "@mrflark/core/api";
import type {
    AcceptSuggestionResponse,
    AiAnalysisIdsResponse,
    AiAnalysisSearchResponse,
    AiModelsResponse,
    AiStatsResponse,
    AllTagsResponse,
    AuthenticatedMeResponse,
    BulkActionRequest,
    BulkActionResponse,
    BulkTagResponse,
    CountResponse,
    CreateUserRequest,
    CreateUserResponse,
    ListUsersResponse,
    ManageItemsResponse,
    MediaItem,
    MediaItemWithCount,
    NextImageRequest,
    OrderedMediaResponse,
    RejectSuggestionResponse,
    SearchRequest,
    SearchResponse,
    SetPrimaryResponse,
    ShareLink,
    ShareListResponse,
    SimilarItemsResponse,
    TagOperationExecuteResponse,
    TagOperationPreviewResponse,
    TagOperationRequest,
    TagQuerySuggestionsResponse,
    TagStatsResponse,
    TagSuggestionsResponse,
    UpdateTagsResponse,
    UpdateUserRequest,
    UpdateUserResponse,
    ValidTagQueryResponse,
    VoteResponse,
} from "@mrflark/spl_service_model";
import type {
    ActionResponse,
    AiAnalysisIdsOptions,
    AiAnalysisItemsOptions,
    AudioClassificationsResponse,
    AudioFingerprintMatchesResponse,
    BulkReanalyzeRequest,
    BulkReanalyzeResponse,
    CreateShareOptions,
    CropRequest,
    CropResponse,
    DuplicateClustersResponse,
    EngagementStats,
    EngagementVoteResponse,
    ExemptDuplicatesResponse,
    FTSResponse,
    GetMediaOptions,
    ImportResponse,
    JobStatsResponse,
    LibraryHealthResponse,
    ManageItemsOptions,
    MergeDuplicatesResponse,
    PaginatedViewedMedia,
    PaginatedVotedMedia,
    ProfileViewsOptions,
    ProfileVotesOptions,
    ReviewItemsResponse,
    ReviewOverrideBody,
    SettingsResponse,
    TagDefinitionsResponse,
    UploadResponse,
    UploadStatusResponse,
    UserVoteInfo,
    VoteType,
    WatermarkAccuracyResponse,
    WatermarkVerdictResponse,
} from "./types.ts";

const DEFAULT_SPL_ENDPOINT = "https://spl-api.mrflark.org";

export class SPLClient extends BaseSdkClient {
    constructor(options?: Partial<BaseSdkClientOptions>) {
        super({
            endpoint: options?.endpoint ?? DEFAULT_SPL_ENDPOINT,
            routePrefix: options?.routePrefix ?? "",
            onUnauthorized: options?.onUnauthorized,
        });
    }

    private mediaOptionsToQuery = (
        options: GetMediaOptions,
    ): Record<string, string | number | string[] | undefined> => {
        return {
            tag_query: options.tagQuery,
            tags: options.tags,
            tag_mode: options.tagMode,
            min_views: options.minViews,
            max_views: options.maxViews,
            min_votes: options.minVotes,
            max_votes: options.maxVotes,
            search_text: options.searchText,
            media_type: options.mediaType,
            exclude_id: Array.isArray(options.excludeId)
                ? options.excludeId.join(",")
                : options.excludeId,
            sort_by: options.sortBy,
            sort_order: options.sortOrder,
            offset: options.offset,
            limit: options.limit,
            has_watermark: options.hasWatermark !== undefined
                ? String(options.hasWatermark)
                : undefined,
            min_ai_confidence: options.minAiConfidence !== undefined
                ? String(options.minAiConfidence)
                : undefined,
        };
    };

    public getMe = (): Promise<AuthenticatedMeResponse> => {
        return this.get<AuthenticatedMeResponse>("/api/auth/me");
    };

    public listUsers = (): Promise<ListUsersResponse> => {
        return this.get<ListUsersResponse>("/api/auth/users");
    };

    public createUser = (
        body: CreateUserRequest,
    ): Promise<CreateUserResponse> => {
        return this.post<CreateUserResponse>("/api/auth/users", body);
    };

    public updateUser = (
        username: string,
        body: UpdateUserRequest,
    ): Promise<UpdateUserResponse> => {
        return this.patch<UpdateUserResponse>(
            `/api/auth/users/${encodeURIComponent(username)}`,
            body,
        );
    };

    public vote = (id: string): Promise<VoteResponse> => {
        return this.post<VoteResponse>("/vote", { id });
    };

    public getNextImage = (
        body: NextImageRequest,
    ): Promise<MediaItemWithCount> => {
        return this.post<MediaItemWithCount>("/next_image", body);
    };

    public getRandomMedia = (
        options: GetMediaOptions = {},
    ): Promise<MediaItemWithCount> => {
        return this.get<MediaItemWithCount>(
            "/api/media/random",
            this.mediaOptionsToQuery(options),
        );
    };

    public getMediaCount = (
        options: GetMediaOptions = {},
    ): Promise<CountResponse> => {
        return this.get<CountResponse>(
            "/api/media/count",
            this.mediaOptionsToQuery(options),
        );
    };

    public getOrderedMedia = (
        options: GetMediaOptions = {},
    ): Promise<OrderedMediaResponse> => {
        return this.get<OrderedMediaResponse>(
            "/api/media/ordered",
            this.mediaOptionsToQuery(options),
        );
    };

    public getMediaItem = (id: string): Promise<MediaItem> => {
        return this.get<MediaItem>(`/api/media/${encodeURIComponent(id)}`);
    };

    public getAudioClassifications = (
        id: string,
    ): Promise<AudioClassificationsResponse> => {
        return this.get<AudioClassificationsResponse>(
            `/api/media/${encodeURIComponent(id)}/audio_classifications`,
        );
    };

    public getAudioFingerprintMatches = (
        id: string,
    ): Promise<AudioFingerprintMatchesResponse> => {
        return this.get<AudioFingerprintMatchesResponse>(
            `/api/media/${encodeURIComponent(id)}/audio_fingerprint_matches`,
        );
    };

    public search = (body: SearchRequest): Promise<SearchResponse> => {
        return this.post<SearchResponse>("/api/search", body);
    };

    public getSimilar = (
        id: string,
        options: { limit?: number; } = {},
    ): Promise<SimilarItemsResponse> => {
        return this.get<SimilarItemsResponse>(
            `/api/media/${encodeURIComponent(id)}/similar`,
            { limit: options.limit },
        );
    };

    public fullTextSearch = (
        searchQuery: string,
        options: { limit?: number; } = {},
    ): Promise<FTSResponse> => {
        return this.get<FTSResponse>("/api/search/fts", {
            q: searchQuery,
            limit: options.limit,
        });
    };

    public updateTags = (
        id: string,
        tags: string[],
    ): Promise<UpdateTagsResponse> => {
        return this.post<UpdateTagsResponse>("/update_tags", { id, tags });
    };

    public addTag = (id: string, tag: string): Promise<UpdateTagsResponse> => {
        return this.post<UpdateTagsResponse>("/add_tag", { id, tag });
    };

    public removeTag = (
        id: string,
        tag: string,
    ): Promise<UpdateTagsResponse> => {
        return this.post<UpdateTagsResponse>("/remove_tag", { id, tag });
    };

    public bulkAddTag = (
        ids: string[],
        tag: string,
    ): Promise<BulkTagResponse> => {
        return this.post<BulkTagResponse>("/bulk_add_tag", { ids, tag });
    };

    public bulkRemoveTag = (
        ids: string[],
        tag: string,
    ): Promise<BulkTagResponse> => {
        return this.post<BulkTagResponse>("/bulk_remove_tag", { ids, tag });
    };

    public getAllTags = (): Promise<AllTagsResponse> => {
        return this.get<AllTagsResponse>("/get_all_tags");
    };

    public getTagStats = (): Promise<TagStatsResponse> => {
        return this.get<TagStatsResponse>("/api/tags/stats");
    };

    public getTagSuggestions = (
        searchQuery?: string,
        limit?: number,
    ): Promise<TagSuggestionsResponse> => {
        return this.get<TagSuggestionsResponse>("/get_tag_suggestions", {
            q: searchQuery,
            limit,
        });
    };

    public validateTagQuery = (
        tagQuery: string,
    ): Promise<ValidTagQueryResponse> => {
        return this.post<ValidTagQueryResponse>("/validate_tag_query", {
            query: tagQuery,
        });
    };

    public getTagQuerySuggestions = (
        tagQuery?: string,
        cursor?: string,
        limit?: number,
    ): Promise<TagQuerySuggestionsResponse> => {
        return this.get<TagQuerySuggestionsResponse>(
            "/get_tag_query_suggestions",
            {
                query: tagQuery,
                cursor,
                limit,
            },
        );
    };

    public acceptSuggestion = (
        id: string,
        tag: string,
    ): Promise<AcceptSuggestionResponse> => {
        return this.post<AcceptSuggestionResponse>(
            "/api/tags/accept_suggestion",
            { id, tag },
        );
    };

    public rejectSuggestion = (
        id: string,
        tag: string,
    ): Promise<RejectSuggestionResponse> => {
        return this.post<RejectSuggestionResponse>(
            "/api/tags/reject_suggestion",
            { id, tag },
        );
    };

    public previewTagOperation = (
        request: TagOperationRequest,
    ): Promise<TagOperationPreviewResponse> => {
        return this.post<TagOperationPreviewResponse>(
            "/api/admin/tags/preview",
            request,
        );
    };

    public executeTagOperation = (
        request: TagOperationRequest,
    ): Promise<TagOperationExecuteResponse> => {
        return this.post<TagOperationExecuteResponse>(
            "/api/admin/tags/execute",
            request,
        );
    };

    public getManageItems = (
        options: ManageItemsOptions = {},
    ): Promise<ManageItemsResponse> => {
        return this.get<ManageItemsResponse>("/api/manage/items", {
            sort_by: options.sortBy,
            sort_order: options.sortOrder,
            view: options.view,
            tag_query: options.tagQuery,
            tags: options.tags,
            tag_mode: options.tagMode,
            page: options.page,
            per_page: options.perPage,
        });
    };

    public bulkAction = (
        body: BulkActionRequest,
    ): Promise<BulkActionResponse> => {
        return this.post<BulkActionResponse>("/api/manage/bulk", body);
    };

    public getReviewItems = (
        limit?: number,
        category?: string,
    ): Promise<ReviewItemsResponse> => {
        return this.get<ReviewItemsResponse>("/api/admin/review", {
            limit,
            category,
        });
    };

    public approveItem = (id: string): Promise<ActionResponse> => {
        return this.post<ActionResponse>(
            `/api/admin/review/${encodeURIComponent(id)}/approve`,
        );
    };

    public getDuplicateClusters = (): Promise<DuplicateClustersResponse> => {
        return this.get<DuplicateClustersResponse>(
            "/api/admin/review/duplicate-clusters",
        );
    };

    public exemptDuplicates = (
        mediaIds: string[],
    ): Promise<ExemptDuplicatesResponse> => {
        return this.post<ExemptDuplicatesResponse>(
            "/api/admin/review/duplicate-clusters/exempt",
            { media_ids: mediaIds },
        );
    };

    public mergeDuplicates = (
        keepId: string,
        deleteIds: string[],
    ): Promise<MergeDuplicatesResponse> => {
        return this.post<MergeDuplicatesResponse>(
            "/api/admin/review/duplicate-clusters/merge",
            { keep_id: keepId, delete_ids: deleteIds },
        );
    };

    public confirmWatermark = (
        id: string,
    ): Promise<WatermarkVerdictResponse> => {
        return this.post<WatermarkVerdictResponse>(
            `/api/admin/review/${encodeURIComponent(id)}/watermark-confirm`,
        );
    };

    public denyWatermark = (id: string): Promise<WatermarkVerdictResponse> => {
        return this.post<WatermarkVerdictResponse>(
            `/api/admin/review/${
                encodeURIComponent(id)
            }/watermark-false-positive`,
        );
    };

    public getWatermarkAccuracy = (): Promise<WatermarkAccuracyResponse> => {
        return this.get<WatermarkAccuracyResponse>(
            "/api/admin/review/watermark-accuracy",
        );
    };

    public rejectItem = (id: string): Promise<ActionResponse> => {
        return this.post<ActionResponse>(
            `/api/admin/review/${encodeURIComponent(id)}/reject`,
        );
    };

    public overrideItem = (
        id: string,
        body: ReviewOverrideBody,
    ): Promise<ActionResponse> => {
        return this.post<ActionResponse>(
            `/api/admin/review/${encodeURIComponent(id)}/override`,
            body,
        );
    };

    public clearReviewQueue = (): Promise<ActionResponse> => {
        return this.post<ActionResponse>("/api/admin/review/clear");
    };

    public getJobStats = (): Promise<JobStatsResponse> => {
        return this.get<JobStatsResponse>("/api/admin/jobs/stats");
    };

    public upload = async (
        files: File[] | Blob[],
        filenames?: string[],
    ): Promise<UploadResponse> => {
        const formData = new FormData();
        for (const [index, file] of files.entries()) {
            const name = filenames?.[index] ?? `file${index}`;
            formData.append("file", file, name);
        }

        // bypass BaseSdkClient.request() which forces JSON content-type
        const url = `${this.endpoint}${this.routePrefix}/api/media/upload`;

        const response = await fetch(url, {
            method: "POST",
            body: formData,
            credentials: "include",
        });

        const body = await response.json().catch(() => null) as
            | Partial<UploadResponse> & { error?: string; }
            | null;

        // a refusal that names every file is a result, not an exception: the
        // caller renders those reasons the same way it renders the partial
        // success a 207 carries. only a response with no per-file account
        // left to give — a gateway page, a network fault — throws.
        if (body?.errors) {
            return {
                items: body.items ?? [],
                total_uploaded: body.total_uploaded ?? 0,
                ...body,
            } as UploadResponse;
        }

        if (!response.ok) {
            throw new Error(
                body?.error ?? `Upload failed (HTTP ${response.status})`,
            );
        }

        return body as UploadResponse;
    };

    public importMedia = (url: string): Promise<ImportResponse> =>
        this.post<ImportResponse>("/api/media/import", { url });

    public getUploadStatus = (
        jobIds: string[],
    ): Promise<UploadStatusResponse> =>
        this.get<UploadStatusResponse>("/api/media/upload/status", {
            job_ids: jobIds.join(","),
        });

    public getAiAnalysisItems = (
        options: AiAnalysisItemsOptions = {},
    ): Promise<AiAnalysisSearchResponse> => {
        return this.get<AiAnalysisSearchResponse>("/api/admin/ai-analysis", {
            page: options.page,
            per_page: options.perPage,
            sort_by: options.sortBy,
            sort_order: options.sortOrder,
            model_name: options.modelName,
            min_confidence: options.minConfidence,
            max_confidence: options.maxConfidence,
            analyzed_after: options.analyzedAfter,
            analyzed_before: options.analyzedBefore,
            description_search: options.descriptionSearch,
            has_analysis: options.hasAnalysis,
            min_analysis_count: options.minAnalysisCount,
            ai_suggested_tag: options.aiSuggestedTag,
            include_videos: options.includeVideos,
        });
    };

    public getAiAnalysisIds = (
        options: AiAnalysisIdsOptions = {},
    ): Promise<AiAnalysisIdsResponse> => {
        return this.get<AiAnalysisIdsResponse>("/api/admin/ai-analysis/ids", {
            sort_by: options.sortBy,
            sort_order: options.sortOrder,
            model_name: options.modelName,
            min_confidence: options.minConfidence,
            max_confidence: options.maxConfidence,
            analyzed_after: options.analyzedAfter,
            analyzed_before: options.analyzedBefore,
            description_search: options.descriptionSearch,
            has_analysis: options.hasAnalysis,
            min_analysis_count: options.minAnalysisCount,
            ai_suggested_tag: options.aiSuggestedTag,
        });
    };

    public getAiModels = (): Promise<AiModelsResponse> => {
        return this.get<AiModelsResponse>("/api/admin/ai-analysis/models");
    };

    public getAiStats = (): Promise<AiStatsResponse> => {
        return this.get<AiStatsResponse>("/api/admin/ai-analysis/stats");
    };

    public reanalyzeMedia = (
        body: BulkReanalyzeRequest,
    ): Promise<BulkReanalyzeResponse> => {
        return this.post<BulkReanalyzeResponse>(
            "/api/admin/ai-analysis/reanalyze",
            body,
        );
    };

    public setAiPrimary = (
        mediaId: string,
        modelName: string,
    ): Promise<SetPrimaryResponse> => {
        return this.post<SetPrimaryResponse>(
            `/api/admin/ai-analysis/${encodeURIComponent(mediaId)}/set-primary`,
            { model_name: modelName },
        );
    };

    public cropMedia = (
        mediaId: string,
        crop: CropRequest,
    ): Promise<CropResponse> => {
        return this.post<CropResponse>(
            `/api/admin/media/${encodeURIComponent(mediaId)}/crop`,
            crop,
        );
    };

    public getTagDefinitions = (): Promise<TagDefinitionsResponse> =>
        this.get<TagDefinitionsResponse>("/api/admin/tag-definitions");

    public setTagDefinition = (
        tag: string,
        aiDescription: string,
    ): Promise<{ success: boolean; }> =>
        this.put<{ success: boolean; }>(
            `/api/admin/tag-definitions/${encodeURIComponent(tag)}`,
            { ai_description: aiDescription },
        );

    public deleteTagDefinition = (
        tag: string,
    ): Promise<{ success: boolean; }> =>
        this.request<{ success: boolean; }>(
            `/api/admin/tag-definitions/${encodeURIComponent(tag)}`,
            { method: "DELETE" },
        );

    public getSettings = (): Promise<SettingsResponse> =>
        this.get<SettingsResponse>("/api/admin/settings");

    public setSetting = (
        key: string,
        value: string,
    ): Promise<{ key: string; value: string; }> =>
        this.put<{ key: string; value: string; }>(
            `/api/admin/settings/${encodeURIComponent(key)}`,
            { value },
        );

    public getLibraryHealth = (): Promise<LibraryHealthResponse> =>
        this.get<LibraryHealthResponse>("/api/admin/health");

    public voteOnMedia = (
        mediaId: string,
        vote: VoteType,
    ): Promise<EngagementVoteResponse> => {
        return this.post<EngagementVoteResponse>(`/api/media/${mediaId}/vote`, {
            vote,
        });
    };

    public removeVote = async (mediaId: string): Promise<void> => {
        await this.request(`/api/media/${mediaId}/vote`, { method: "DELETE" });
    };

    public getMyVote = (mediaId: string): Promise<UserVoteInfo> => {
        return this.get<UserVoteInfo>(`/api/media/${mediaId}/vote`);
    };

    public getMyVotes = async (
        mediaIds: string[],
    ): Promise<Record<string, VoteType>> => {
        const result = await this.get<{ votes: Record<string, VoteType>; }>(
            `/api/media/votes?ids=${mediaIds.join(",")}`,
        );
        return result.votes;
    };

    public getProfileVotes = (
        userId: string,
        options?: ProfileVotesOptions,
    ): Promise<PaginatedVotedMedia> => {
        const params = new URLSearchParams();
        if (options?.vote) params.set("vote", options.vote);
        if (options?.cursor) params.set("cursor", options.cursor);
        if (options?.limit) params.set("limit", String(options.limit));
        const qs = params.toString();
        return this.get<PaginatedVotedMedia>(
            `/api/profile/${userId}/votes${qs ? `?${qs}` : ""}`,
        );
    };

    public getProfileViews = (
        userId: string,
        options?: ProfileViewsOptions,
    ): Promise<PaginatedViewedMedia> => {
        const params = new URLSearchParams();
        if (options?.cursor) params.set("cursor", options.cursor);
        if (options?.limit) params.set("limit", String(options.limit));
        const qs = params.toString();
        return this.get<PaginatedViewedMedia>(
            `/api/profile/${userId}/views${qs ? `?${qs}` : ""}`,
        );
    };

    public getProfileStats = (userId: string): Promise<EngagementStats> => {
        return this.get<EngagementStats>(`/api/profile/${userId}/stats`);
    };

    public getProfileRecommendations = (
        userId: string,
        limit?: number,
    ): Promise<{ items: MediaItem[]; }> => {
        const qs = limit ? `?limit=${limit}` : "";
        return this.get<{ items: MediaItem[]; }>(
            `/api/profile/${userId}/recommendations${qs}`,
        );
    };

    public createShareLink = (
        mediaId: string,
        options?: CreateShareOptions,
    ): Promise<ShareLink> => {
        return this.post<ShareLink>(
            `/api/media/${encodeURIComponent(mediaId)}/share`,
            options ?? {},
        );
    };

    public getMediaShares = (mediaId: string): Promise<ShareListResponse> => {
        return this.get<ShareListResponse>(
            `/api/media/${encodeURIComponent(mediaId)}/shares`,
        );
    };

    public listMyShares = (): Promise<ShareListResponse> => {
        return this.get<ShareListResponse>("/api/shares");
    };

    public revokeShareLink = async (shareId: string): Promise<void> => {
        await this.request(`/api/shares/${encodeURIComponent(shareId)}`, {
            method: "DELETE",
        });
    };
}
